// Generated by Claude Code
import { WorkRequestDetailPanel } from '#components/domain/operations/work-requests/work-request-detail/index.tsx'
import { ReviewWorkRequestDrawer } from '#components/domain/operations/work-requests/work-request-edit/review-work-request-drawer.tsx'
import { PermissionGuard } from '#components/tools/permission-guard.tsx'
import WorkOrderRequestsApi from '#core/api/operations/work-orders/WorkOrderRequests.ts'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { readAnyGroups } from '#core/entities/auth/roles/permission-gate-helpers.ts'
import { useUserState } from '#core/state/UserState.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_app/$companyId/work-requests/$requestId')({
  component: RouteComponent,
  staticData: {
    title: 'Work Request Detail',
  },
})

function RouteComponent() {
  return (
    <PermissionGuard
      permissions={[
        ...readAnyGroups(PermissionService.WORK_ORDER_REQUESTS),
        [[PermissionService.WORK_ORDER_REQUESTS, PermissionAction.CREATE]],
      ]}
      redirectOnNotAuthorized
    >
      <WorkRequestDetailPageContent />
    </PermissionGuard>
  )
}

function WorkRequestDetailPageContent() {
  const { companyId, requestId } = Route.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation('work-requests')
  const userState = useUserState()

  // Load the request just to route between the editable review (open requests) and the
  // read-only decided view. The panel/drawer each self-load their own record afterwards.
  const [status, setStatus] = useState<'loading' | 'open' | 'decided' | 'error'>('loading')

  function handleBack() {
    void navigate({ to: '/$companyId/work-requests', params: { companyId } })
  }

  useEffect(() => {
    let cancelled = false
    const mineOnly = userState.isLimitedViewOnly(PermissionService.WORK_ORDER_REQUESTS)
    const api = new WorkOrderRequestsApi()
    void (async () => {
      const wr = await (mineOnly ? api.getMineWorkRequest(requestId) : api.getWorkRequest(requestId)).catch(() => null)
      if (cancelled) return
      setStatus(!wr ? 'error' : wr.request_status === 'request' ? 'open' : 'decided')
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId])

  return (
    <div className="container-fluid d-flex flex-column h-100 py-3">
      <div className="row justify-content-center flex-grow-1">
        <div className="col-12 col-lg-10 col-xl-8 d-flex flex-column">
          <div className="mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
              onClick={handleBack}
            >
              <ArrowLeft size={14} />
              {t('shared.actions.backToList')}
            </button>
          </div>
          {status === 'decided' && (
            <div className="flex-grow-1 overflow-hidden card rounded-3 p-0">
              <WorkRequestDetailPanel requestId={requestId} onClose={handleBack} onDeleted={handleBack} />
            </div>
          )}
          {status === 'error' && (
            <div className="alert alert-danger">{t('detail.errorLoad')}</div>
          )}
        </div>
      </div>

      {/* Open requests are reviewed in the editable drawer, mirroring the list page. */}
      <ReviewWorkRequestDrawer
        isOpen={status === 'open'}
        requestId={requestId}
        onClose={handleBack}
        onAccepted={handleBack}
        onDeclined={handleBack}
      />
    </div>
  )
}
